import { add, fromNumber, type MonetaryValue, toNumber } from 'ezmoney';
import pThrottle from 'p-throttle';
import { useQueries } from 'react-query';
import { useLocation } from 'react-router-dom';

import { postV2ApiCompanyIdInternationalTransfersQuotes } from 'src/client/spendesk-api';
import { type PostV2ApiCompanyIdInternationalTransfersQuotesResponseOK } from 'src/client/spendesk-api-types';
import { isInternationalTransfer } from 'src/core/utils';

import { useIsIwtEnabled } from './useIsIwtEnabled';
import { type ExpenseBill, type InvoiceBill } from '../../../models';
import { getIsCountryAndCurrencySupported } from '../../../utils/isCountryAndCurrencySupported';
import { type Transfer } from '../../models';

export type TransferQuote =
  PostV2ApiCompanyIdInternationalTransfersQuotesResponseOK['data'] & {
    transferId: string;
  };

export const VALIDATION_ERROR_CAUSE = 'ValidationError';

export const QUOTE_KEY = 'quotes';

async function fetchQuote({
  companyId,
  targetCurrency,
  targetAmount,
  sourceCurrency,
  recipientAccountId,
  transferId,
  unauthenticated,
}: {
  transferId: string;
  targetCurrency: string;
  targetAmount: number;
  sourceCurrency: string;
  recipientAccountId: string | undefined;
  companyId: string;
  unauthenticated?: boolean;
}) {
  const response = await postV2ApiCompanyIdInternationalTransfersQuotes({
    path: { companyId },
    body: {
      sourceCurrency,
      targetAmount,
      targetCurrency,
      recipientAccountId,
      unauthenticated,
    },
  });

  // Check for a 502 Bad Gateway error to extract the validation partner errors.
  if (response.statusCode === 502) {
    const validationErrors = response.body.validationErrors;

    if (validationErrors?.length) {
      throw new Error(
        validationErrors
          .map((validationError) => validationError.message)
          .join(', '),
        { cause: VALIDATION_ERROR_CAUSE },
      );
    }
  }

  if (response.statusCode !== 200) {
    throw new Error(`Failed to create quote for transfer ${transferId}`);
  }

  return { ...response.body.data, transferId };
}

export function useQuotes({
  selectedTransfers,
  companyCurrency,
  companyId,
  onError,
}: {
  companyCurrency: string;
  companyId: string;
  selectedTransfers: Array<{
    id: string;
    bill: InvoiceBill | ExpenseBill | undefined;
    amount: MonetaryValue;
  }>;
  onError?: (error: unknown) => void;
}) {
  const location = useLocation();
  const isConfirmPage =
    location.pathname === `/app/${companyId}/invoices/confirm`;

  const isIwtEnabled = useIsIwtEnabled();

  // Wrap the API requests with a throttle of 2 seconds a request to avoid hitting
  // rate limits on our quote provider.
  const throttle = pThrottle({
    limit: 2,
    interval: 1000,
  });

  const throttledFetchQuote = throttle(
    async (transfer: {
      id: string;
      bill: { counterparty: { id: string } } | undefined;
      amount: MonetaryValue;
    }) =>
      fetchQuote({
        targetAmount: toNumber(transfer.amount),
        targetCurrency: transfer.amount.currency,
        transferId: transfer.id,
        sourceCurrency: companyCurrency,
        recipientAccountId: transfer.bill?.counterparty?.id,
        companyId,
        unauthenticated: isConfirmPage,
      }),
  );

  // Fetch quotes for selected transfers using an array of queries.
  // The queries are cached by the transfer id for 30 minutes to match
  // the Wise quote expiration time.
  const queries = useQueries(
    selectedTransfers.map((transfer) => ({
      queryKey: [QUOTE_KEY, transfer.id],
      queryFn: () => throttledFetchQuote(transfer),
      enabled:
        isIwtEnabled &&
        getIsCountryAndCurrencySupported(transfer as Transfer) &&
        isInternationalTransfer({
          sourceCurrency: companyCurrency,
          targetCurrency: transfer.amount.currency,
          targetCountry: transfer?.bill?.counterparty?.bankAddress?.country,
        }),
      cacheTime: 0.5 * 60 * 60 * 1000, // Cache for 30 minutes,
      staleTime: 0.5 * 60 * 60 * 1000, // Fresh for 30 minutes,
      retry: false,
      refetchOnWindowFocus: false,
      onError: (error: unknown) => {
        if (onError) {
          onError(error);
        }
      },
    })),
  );

  const isLoading = queries.some((query) => query.isLoading);

  const quotes: TransferQuote[] = queries
    .map((query) => query.data)
    .filter((data) => data) as TransferQuote[]; // Filter out undefined results

  const hasError = queries.some((query) => query.isError);

  const hasDisabledQuotes = quotes.some((it) => it.paymentOptions[0]?.disabled);

  // Map the quotes payment options
  const quotePaymentOptions = quotes
    .filter((quote) => quote.paymentOptions.length > 0)
    .map((quote) => quote.paymentOptions[0]);

  const totalFees = quotePaymentOptions.reduce(
    (accumulator, value) => {
      const fee = fromNumber(value.fees.total, companyCurrency, 2);

      return add(accumulator, fee);
    },
    fromNumber(0, companyCurrency, 2),
  );

  // Calculate the total amount of non international transfers.
  const totalAmountCompanyCurrency = selectedTransfers
    .filter(
      (transfer) =>
        !isInternationalTransfer({
          targetCurrency: transfer.amount.currency,
          sourceCurrency: companyCurrency,
          targetCountry: transfer.bill?.counterparty.bankAddress?.country,
        }),
    )
    .reduce<MonetaryValue>(
      (accumulator, transfer) => {
        return add(accumulator, transfer.amount);
      },
      fromNumber(0, companyCurrency, 2),
    );

  // Calculate the total amount with the quote source amounts.
  const totalAmountWithQuotes = quotePaymentOptions.reduce(
    (accumulator, value) => {
      const amount = fromNumber(value.sourceAmount, companyCurrency, 2);

      const internalFees = fromNumber(
        value.fees.internalBanking,
        companyCurrency,
        2,
      );

      const amountWithFees = add(amount, internalFees);

      return add(accumulator, amountWithFees);
    },
    totalAmountCompanyCurrency,
  );

  return {
    isLoading,
    quotes,
    hasError,
    hasDisabledQuotes,
    totalAmountWithQuotes,
    totalFees,
  };
}
